import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  createDepartmentOfCompanyPayload,
  DeleteDepartmentOfCompanyPayload,
  DepartmentType,
  UpdateManagerDepartmentsAccessPayload,
} from "./departmentsApi";

export interface DepartmentsState {
  departments: DepartmentType[];
  getDepartmentsByCompanyStatus: QueryStatus;
  updateManagerDepartmentsAccessStatus: QueryStatus;
  deleteDepartmentOfCompanyStatus: QueryStatus;
}

const initialState: DepartmentsState = {
  departments: [],
  getDepartmentsByCompanyStatus: "idle",
  updateManagerDepartmentsAccessStatus: "idle",
  deleteDepartmentOfCompanyStatus: "idle",
};

export const getDepartmentsByCompanyAsync = createAsyncThunk(
  "departments/getDepartmentsByCompany",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<DepartmentType[]>(
      `departments/company/${companyUuid}`,
    );
    return response.data;
  },
);

export const updateManagerDepartmentsAccessAsync = createAsyncThunk(
  "departments/updateManagerDepartmentsAccess",
  async (payload: UpdateManagerDepartmentsAccessPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(
      `/manager/${payload.managerUuid}/update_departments_access`,
      payload,
    );
  },
);

export const createDepartmentOfCompanyAsync = createAsyncThunk(
  "/department",
  async (payload: createDepartmentOfCompanyPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/department`, payload);
  },
)

export const deleteDepartmentOfCompanyAsync = createAsyncThunk(
  "departments/deleteDepartmentOfCompany",
  async (payload: DeleteDepartmentOfCompanyPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/departments/delete_department_of_company`, payload);
  },
);

export const departmentsSlice = createSlice({
  name: "departments",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetDepartmentsListStatus(state) {
      state.getDepartmentsByCompanyStatus = "idle";
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentsByCompanyAsync.pending, (state) => {
        state.getDepartmentsByCompanyStatus = "processing";
      })
      .addCase(getDepartmentsByCompanyAsync.fulfilled, (state, action) => {
        state.getDepartmentsByCompanyStatus = "success";
        state.departments = action.payload;
      })
      .addCase(getDepartmentsByCompanyAsync.rejected, (state) => {
        state.getDepartmentsByCompanyStatus = "failed";
      })
      .addCase(updateManagerDepartmentsAccessAsync.pending, (state) => {
        state.updateManagerDepartmentsAccessStatus = "processing";
      })
      .addCase(updateManagerDepartmentsAccessAsync.fulfilled, (state) => {
        state.updateManagerDepartmentsAccessStatus = "success";
      })
      .addCase(updateManagerDepartmentsAccessAsync.rejected, (state) => {
        state.updateManagerDepartmentsAccessStatus = "failed";
      })
      .addCase(createDepartmentOfCompanyAsync.pending, (state) => {
        state.deleteDepartmentOfCompanyStatus = "processing";
      })
      .addCase(createDepartmentOfCompanyAsync.fulfilled, (state) => {
        state.deleteDepartmentOfCompanyStatus = "success";
      })
      .addCase(createDepartmentOfCompanyAsync.rejected, (state) => {
        state.deleteDepartmentOfCompanyStatus = "failed";
      })
      .addCase(deleteDepartmentOfCompanyAsync.pending, (state) => {
        state.deleteDepartmentOfCompanyStatus = "processing";
      })
      .addCase(deleteDepartmentOfCompanyAsync.fulfilled, (state) => {
        state.deleteDepartmentOfCompanyStatus = "success";
      })
      .addCase(deleteDepartmentOfCompanyAsync.rejected, (state) => {
        state.deleteDepartmentOfCompanyStatus = "failed";
      });
  },
});

export const selectGetDepartmentsByCompanyStatus = (state: RootState) =>
  state.departments.getDepartmentsByCompanyStatus;

export const selectDepartments = (state: RootState) =>
  state.departments.departments;

export const selectUpdateManagerDepartmentsAccessStatus = (state: RootState) =>
  state.departments.updateManagerDepartmentsAccessStatus;

export const selectCreateDepartmentOfCompanyStatus = (state: RootState) =>
  state.departments.deleteDepartmentOfCompanyStatus;

export const selectDeleteDepartmentOfCompanyStatus = (state: RootState) =>
  state.departments.deleteDepartmentOfCompanyStatus;

export default departmentsSlice.reducer;
